import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import {
  callSurfaceMode,
  claimDesktopCallWindow,
  focusDesktopCallWindow,
  isDesktopCallWindow,
  openDesktopCallWindow,
} from '@/features/desktop/callWindow';
import type { DesktopCallWindowRequest } from '@/features/desktop/types';
import { activeCallActions } from '../store/activeCallStore';
import { liveCallActions } from '../store/liveCallStore';

export type OpenCallRequest = DesktopCallWindowRequest;

/**
 * THE ONE WAY INTO A CALL.
 *
 * There are seven places that put you in one — placing, answering the in-app
 * card, answering the native ringer, following a completed transfer or merge,
 * starting the consult leg of an attended transfer, backing out of one, and
 * landing on `/call/:id` from a link. Every one of them used to be a bare
 * `liveCallActions.enter` + `navigate` pair, and four of them were only the
 * `navigate` half, relying on `CallPage` to do the `enter` on arrival.
 *
 * That asymmetry is exactly what makes "open the call in its own window" a trap
 * to bolt on: branch inside `enter` and the four sites that never call it are
 * missed; branch inside `CallPage` and the call opens inline first and pops out
 * afterwards, which is a visible flash of the call in the workspace window. So
 * the decision lives here, and all seven ask.
 *
 *   inline  → today's two lines, verbatim. Every browser session, and the
 *             desktop CALL window, where this document already IS the call.
 *   window  → hand it to the shell. The workspace window does not navigate at
 *             all: you stay in the conversation you were reading.
 *
 * ONE EXTRA STEP INSIDE A CALL WINDOW, and it is load-bearing. That window is
 * keyed in the shell by its own identity, with the call it holds as a mutable
 * property, because two ordinary buttons in its own UI — "Call again" and the
 * consult leg of an attended transfer — move it to a DIFFERENT call. So it
 * CLAIMS the new call before it navigates, and a refusal means another live
 * window already holds it: focus that one instead of opening a second media
 * session against the same SFU room.
 */
export function useOpenCall(): (request: OpenCallRequest) => void {
  const navigate = useNavigate();

  const enter = useCallback(
    (callId: string) => {
      liveCallActions.enter(callId);
      navigate(ROUTES.call(callId));
    },
    [navigate],
  );

  return useCallback(
    (request: OpenCallRequest) => {
      if (callSurfaceMode() === 'inline') {
        /* A BROWSER TAKES EXACTLY THE PATH IT ALWAYS DID — the same two lines,
           synchronously, with no promise between the click and the navigation.
           There is no shell bookkeeping to keep straight there, and deferring
           by even a microtask would change the one path this whole feature is
           supposed to leave alone. */
        if (!isDesktopCallWindow()) {
          enter(request.callId);
          return;
        }
        /* Inside a call window, the shell has to agree that this window may
           move to that call before it does. A refusal means another live window
           already holds it. */
        void claimDesktopCallWindow(request.callId).then((held) => {
          if (held) {
            enter(request.callId);
            return;
          }
          focusDesktopCallWindow(request.callId);
        });
        return;
      }
      /* The grant that placing or answering just minted cannot travel: it is
         in a per-document store the call window has no view of, and that
         window mints its own from `POST /calls/:id/join` on the way up. Drop
         it rather than leaving live SFU credentials sitting in the memory of a
         window that will never use them. */
      activeCallActions.clear(request.callId);
      /* The shell focuses a window that already holds this call rather than
         opening a second one against the same room — see main/inCallWindow.ts,
         which looks the call up by what each window currently HOLDS. */
      openDesktopCallWindow(request);
    },
    [enter],
  );
}
